import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Buildings, Check, PencilSimple, Globe, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublisherProfile() {
  const { publisherId } = useParams();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/profile`, { withCredentials: true })
      .then(r => { setProfile(r.data); setForm(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [publisherId]);

  const save = async () => {
    const allowed = ["name", "description", "website_url", "logo_url", "cover_image_url", "support_email", "visibility_status"];
    const updates = {};
    allowed.forEach(k => { if (form[k] !== profile[k]) updates[k] = form[k]; });
    if (!Object.keys(updates).length) { setEditing(false); return; }
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/profile`, updates, { withCredentials: true });
      toast.success('Profile updated');
      setProfile({ ...profile, ...updates });
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="h-64 bg-white/5 rounded-xl animate-pulse" />;
  if (!profile) return <p className="text-[#666]">Failed to load profile.</p>;

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#F59E0B]/50 focus:outline-none placeholder-[#444]";

  return (
    <div data-testid="publisher-profile-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Publisher Profile</h1>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/[0.08] text-[#888] text-sm hover:text-white transition-colors"
            data-testid="edit-profile-btn"><PencilSimple size={16} /> Edit</button>
        ) : (
          <button onClick={save} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm"
            data-testid="save-profile-btn"><Check size={16} /> Save</button>
        )}
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 space-y-5">
        {/* Status badges */}
        <div className="flex gap-2">
          {profile.verified && (
            <span className="text-[11px] px-3 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] font-medium border border-[#10B981]/20">Verified</span>
          )}
          <span className="text-[11px] px-3 py-1 rounded-full bg-white/5 text-[#888] capitalize">{profile.visibility_status || 'public'}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Publisher Name</label>
            {editing ? <input className={inputCls} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
              : <p className="text-white font-medium">{profile.name}</p>}
          </div>
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Support Email</label>
            {editing ? <input className={inputCls} type="email" value={form.support_email || ''} onChange={e => setForm({ ...form, support_email: e.target.value })} />
              : <p className="text-white flex items-center gap-2"><Envelope size={14} className="text-[#555]" />{profile.support_email || '-'}</p>}
          </div>
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Website</label>
            {editing ? <input className={inputCls} value={form.website_url || ''} onChange={e => setForm({ ...form, website_url: e.target.value })} />
              : <p className="text-white flex items-center gap-2"><Globe size={14} className="text-[#555]" />{profile.website_url || '-'}</p>}
          </div>
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Visibility</label>
            {editing ? (
              <select className={inputCls} value={form.visibility_status || 'public'} onChange={e => setForm({ ...form, visibility_status: e.target.value })}>
                <option value="public">Public</option>
                <option value="hidden">Hidden</option>
              </select>
            ) : <p className="text-white capitalize">{profile.visibility_status || 'public'}</p>}
          </div>
        </div>

        <div>
          <label className="text-xs text-[#666] mb-1.5 block font-medium">Description</label>
          {editing ? <textarea className={inputCls + " h-24 resize-y"} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
            : <p className="text-sm text-[#888]">{profile.description || '-'}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Logo URL</label>
            {editing ? <input className={inputCls} value={form.logo_url || ''} onChange={e => setForm({ ...form, logo_url: e.target.value })} />
              : <p className="text-xs text-[#555] truncate">{profile.logo_url || 'Not set'}</p>}
          </div>
          <div>
            <label className="text-xs text-[#666] mb-1.5 block font-medium">Cover Image URL</label>
            {editing ? <input className={inputCls} value={form.cover_image_url || ''} onChange={e => setForm({ ...form, cover_image_url: e.target.value })} />
              : <p className="text-xs text-[#555] truncate">{profile.cover_image_url || 'Not set'}</p>}
          </div>
        </div>

        <div className="pt-3 border-t border-white/[0.04] text-xs text-[#444]">
          <p>Slug: {profile.slug}</p>
          <p>Created: {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}</p>
        </div>
      </div>
    </div>
  );
}
